import {
  abs,
  float,
  floor,
  Fn,
  fract,
  min,
  positionGeometry,
  sin,
  smoothstep,
  step,
  time,
  vec2,
  vec4,
} from "three/tsl"
import * as THREE from "three/webgpu"

export const plusPattern = Fn(
  ({
    color,
    spacing,
    size,
    visibility,
    blinkSpeed,
  }: {
    color: THREE.UniformNode<THREE.Color>
    spacing: THREE.UniformNode<number>
    size: THREE.UniformNode<number>
    visibility: THREE.UniformNode<number>
    blinkSpeed: THREE.UniformNode<number>
  }) => {
    const patternCoords = positionGeometry.yz

    const gridCell = patternCoords.div(spacing)
    const cellLocal = fract(gridCell)
    const cellIndex = floor(gridCell) // Integer cell coordinates for hashing

    // Center the pattern
    const centered = cellLocal.sub(vec2(0.5))

    // Create plus pattern: horizontal and vertical lines that intersect
    // Horizontal line: centered at y=0, extending in x direction
    // Vertical line: centered at x=0, extending in y direction

    const halfCell = 0.5

    // Horizontal line: thin line at y=0, extending horizontally
    // Thickness: visible when abs(centered.y) is small (close to 0)
    // Use inverted smoothstep: 1 - smoothstep(0, size, abs(centered.y))
    const horizontalThickness = float(1).sub(
      smoothstep(float(0), size, abs(centered.y)),
    )
    // Width: visible when abs(centered.x) is less than halfCell
    const horizontalWidth = float(1).sub(
      smoothstep(float(0), halfCell, abs(centered.x)),
    )
    const horizontalLine = horizontalThickness.mul(horizontalWidth)

    // Vertical line: thin line at x=0, extending vertically
    // Thickness: visible when abs(centered.x) is small (close to 0)
    const verticalThickness = float(1).sub(
      smoothstep(float(0), size, abs(centered.x)),
    )
    // Height: visible when abs(centered.y) is less than halfCell
    const verticalHeight = float(1).sub(
      smoothstep(float(0), halfCell, abs(centered.y)),
    )
    const verticalLine = verticalThickness.mul(verticalHeight)

    // Combine both lines to create plus symbol (union operation)
    const plusMask = min(float(1), horizontalLine.add(verticalLine))

    // Hash calculation for visibility - using improved hash for better randomness
    const hash1 = fract(cellIndex.x.mul(127.1).add(cellIndex.y.mul(311.7)))
    const hash2 = fract(cellIndex.x.mul(269.5).add(cellIndex.y.mul(183.3)))
    const cellHash = fract(hash1.mul(43758.5453).add(hash2))
    // Show cell if hash < visibility threshold
    const isVisible = step(visibility, cellHash).oneMinus()

    // Add blinking effect - each cell blinks independently based on cell position
    // Use completely separate hash with different coefficients to ensure unique phase per cell
    const phaseHash1 = fract(cellIndex.x.mul(97.1).add(cellIndex.y.mul(173.3)))
    const phaseHash2 = fract(cellIndex.x.mul(241.7).add(cellIndex.y.mul(389.5)))
    const phaseHash = fract(
      phaseHash1.mul(43758.5453).add(phaseHash2.mul(7919.0)),
    )
    const phaseOffset = phaseHash.mul(Math.PI * 2)
    const blinkValue = sin(time.mul(blinkSpeed).add(phaseOffset))
    // Normalize sin output from -1 to 1 range to 0.3-1 range (clamp bottom to 0.3)
    const blinkMask = blinkValue.add(1).mul(0.35).add(0.3) // Maps -1..1 to 0.3..1

    // Apply visibility and blinking to mask
    return vec4(color, plusMask.mul(isVisible).mul(blinkMask))
  },
)
